import { constants } from '../../../../configs/constants';

const LLM_BASE_URL = constants.API_URL.replace('/api', '');

/* Settings come from getSettings() (a ref the parent keeps current), NOT from
   run()'s `config` — useLocalRuntime builds config from registered
   ModelContext providers (we register none) and silently discards any
   adapters.chatModel entry, so config is always {} here. */
export function createChatAdapter(getToken, getSettings) {
  return {
    async *run({ messages, abortSignal }) {
      const token = getToken();
      const settings = getSettings?.() || {};
      const model = settings.model ?? 'gpt-4o-mini';
      const temperature = settings.temperature ?? 0.7;
      const maxTokens = settings.maxTokens ?? 4096;
      const systemPrompt = settings.systemPrompt;

      const apiMessages = [];
      if (systemPrompt) {
        apiMessages.push({ role: 'system', content: systemPrompt });
      }

      for (const m of messages) {
        const textParts = m.content.filter(c => c.type === 'text');
        const text = textParts.map(c => c.text).join('\n');
        if (text) {
          apiMessages.push({ role: m.role, content: text });
        }
      }

      const response = await fetch(`${LLM_BASE_URL}/llm/v1/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          model,
          messages: apiMessages,
          temperature,
          max_tokens: maxTokens,
          stream: true,
        }),
        signal: abortSignal,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.error?.message || `API error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const data = trimmed.slice(6);
          if (data === '[DONE]') break;

          try {
            const parsed = JSON.parse(data);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              fullText += delta;
              yield { content: [{ type: 'text', text: fullText }] };
            }
          } catch {
            // skip malformed chunks
          }
        }
      }
    },
  };
}
